import React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { normalizeError } from "@/lib/normalizeError";
import { Loader2, Presentation } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

interface GenerateDeckDialogProps {
  ideaId: string;
  ideaTitle: string;
}

export const GenerateDeckDialog = ({ ideaId, ideaTitle }: GenerateDeckDialogProps) => {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [audience, setAudience] = useState("VC");
  const [tone, setTone] = useState("concise");
  const [slideCount, setSlideCount] = useState([10]);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleGenerate = async () => {
    try {
      setGenerating(true);

      const { data, error } = await supabase.functions.invoke("generate-pitch-deck", {
        body: {
          ideaId,
          audience,
          tone,
          slideCount: slideCount[0],
        },
      });

      if (error) throw error;

      toast({
        title: "Deck Generated!",
        description: `Created ${slideCount[0]}-slide pitch deck`,
      });

      setOpen(false);
      navigate(`/deck/${data.deck.id}`);
    } catch (err) {
      const e = normalizeError(err);
      console.error("Error generating deck:", e.name, e.message, e.raw);
      toast({
        title: "Error",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="w-full">
          <Presentation className="h-3 w-3 mr-1" />
          Generate Pitch Deck
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate Pitch Deck</DialogTitle>
          <DialogDescription>
            Create an AI-powered pitch deck for "{ideaTitle}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="audience">Target Audience</Label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger id="audience">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Angel">Angel Investors</SelectItem>
                <SelectItem value="VC">Venture Capital</SelectItem>
                <SelectItem value="Accelerator">Accelerators</SelectItem>
                <SelectItem value="Customers">Customers</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="tone">Presentation Tone</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger id="tone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="concise">Concise</SelectItem>
                <SelectItem value="visionary">Visionary</SelectItem>
                <SelectItem value="data-driven">Data-Driven</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="slides">Number of Slides: {slideCount[0]}</Label>
            <Slider
              id="slides"
              min={6}
              max={14}
              step={1}
              value={slideCount}
              onValueChange={setSlideCount}
              className="mt-2"
            />
          </div>
        </div>

        <Button onClick={handleGenerate} disabled={generating} className="w-full">
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Presentation className="h-4 w-4 mr-2" />
              Generate Deck
            </>
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
